import React, { useState, useEffect } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { usePopup } from '@/context/PopupContext';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { getGeoLocation, submitLead } from '@/services/api';
import { ArrowRight, Shield, Clock, Award, Users, Check, TrendingUp, Lock, Target } from 'lucide-react';
import { Pricing } from '@/components/sections/Pricing';
import { Testimonials } from '@/components/sections/Testimonials';
import { Process } from '@/components/sections/Process';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const formSchema = z.object({
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must not exceed 50 characters'),
  email: z.string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address (e.g., user@example.com)'),
  country_code: z.string(),
  contact_number: z.string()
    .min(10, 'Phone number must be exactly 10 digits after country code')
    .max(10, 'Phone number must be exactly 10 digits (no more than 10)')
    .regex(/^\d{10}$/, 'Phone number must contain exactly 10 digits only (alphabets and special characters are not allowed)'),
  message: z.string()
    .min(10, 'Message must be at least 10 characters')
    .max(500, 'Message must not exceed 500 characters'),
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const services = [
  {
    icon: Shield,
    title: 'Trademark Registration',
    description: 'Comprehensive trademark search, filing, and registration services to protect your brand identity.'
  },
  {
    icon: Award,
    title: 'Copyright Protection',
    description: 'Secure your creative works with professional copyright registration and enforcement assistance.'
  },
  {
    icon: Users,
    title: 'Patent Services',
    description: 'Expert patent application preparation, filing, and legal representation for your innovations.'
  },
  {
    icon: Clock,
    title: 'Company Formation',
    description: 'Complete business formation services including LLC, C-Corp, S-Corp, and Non-Profit setup.'
  }
];

const faqs = [
  {
    question: 'How long does the trademark registration process take?',
    answer: 'The typical trademark registration process takes 8-12 months from filing to registration. However, you can start using the TM symbol immediately after filing.'
  },
  {
    question: 'What\'s included in the government filing fee?',
    answer: 'The USPTO charges a government filing fee of $250-$350 per class of goods/services. This fee is separate from our service fees and is paid directly to the government.'
  },
  {
    question: 'Can I trademark a business name?',
    answer: 'Yes, you can trademark a business name if it\'s distinctive and used in commerce. We\'ll conduct a comprehensive search to ensure your name is available for registration.'
  },
  {
    question: 'What happens if my trademark application is rejected?',
    answer: 'If your application receives an office action, our legal team will work with you to address the examiner\'s concerns and respond appropriately to overcome any objections.'
  },
  {
    question: 'Do you offer refunds?',
    answer: 'We offer a satisfaction guarantee. If we cannot successfully file your trademark application, we\'ll provide a full refund of our service fees (government fees are non-refundable).'
  },
  {
    question: 'What can be trademarked?',
    answer: 'You can trademark brand names, logos, slogans, product names, service marks, and even specific colors, sounds, or packaging designs that identify your brand.'
  },
  {
    question: 'How much does trademark registration cost?',
    answer: 'Our service fees start at $45, plus USPTO government filing fees of $250-$350 per class. We offer transparent pricing with packages to fit different needs and budgets.'
  },
  {
    question: 'Can I file a trademark myself?',
    answer: 'While you can file directly with USPTO, our expertise ensures proper classification, comprehensive searches, professional responses to office actions, and significantly higher approval rates.'
  },
  {
    question: 'What\'s the difference between TM and ®?',
    answer: 'TM indicates you\'re claiming rights to a mark (can be used immediately), while ® indicates your trademark is officially registered with the USPTO (only after registration is complete).'
  },
  {
    question: 'How long does trademark protection last?',
    answer: 'A registered trademark can last indefinitely as long as you maintain proper use and file required maintenance documents at 5-6 years, 9-10 years, and every 10 years thereafter.'
  }
];

export const HomePage = () => {
  const { openPopup } = usePopup();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoData, setGeoData] = useState(null);
  
  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      country_code: '+1'
    }
  });

  // Fetch geolocation data on component mount
  useEffect(() => {
    const fetchGeoData = async () => {
      const data = await getGeoLocation();
      setGeoData(data);
      
      // Auto-select country code based on geolocation
      if (data?.data?.countryCode) {
        const countryCodeMap = {
          'US': '+1', 'GB': '+44', 'IN': '+91', 'AU': '+61', 'CN': '+86',
          'JP': '+81', 'DE': '+49', 'FR': '+33', 'IT': '+39', 'ES': '+34',
          'RU': '+7', 'BR': '+55', 'MX': '+52', 'KR': '+82', 'NL': '+31',
          'SE': '+46', 'CH': '+41', 'NO': '+47', 'DK': '+45', 'PL': '+48',
          'SG': '+65', 'MY': '+60', 'TH': '+66', 'VN': '+84', 'ID': '+62',
          'PH': '+63', 'PK': '+92', 'BD': '+880', 'AE': '+971', 'SA': '+966',
          'ZA': '+27', 'NG': '+234', 'EG': '+20', 'KE': '+254', 'NZ': '+64',
          'IE': '+353', 'PT': '+351', 'GR': '+30', 'BE': '+32', 'AT': '+43', 'FI': '+358'
        };
        const dialCode = countryCodeMap[data.data.countryCode];
        if (dialCode) {
          setValue('country_code', dialCode);
        }
      }
    };
    
    fetchGeoData();
  }, [setValue]);

  // Slider settings for mobile
  const mobileSliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
    pauseOnHover: true
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const result = await submitLead(data, geoData);
      
      // Handle 200 Success - Redirect to application page
      if (result.success && result.statusCode === 200 && result.customerId) {
        toast.success('Form submitted successfully! Redirecting...');
        reset();
        // Use window.location.href for full page reload
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } 
      // Handle 401 Unauthorized
      else if (result.statusCode === 401) {
        toast.error(result.message || 'Domain not configured. Please contact support.');
      }
      // Handle 400 Bad Request
      else if (result.statusCode === 400) {
        toast.error(result.message || 'Invalid data provided. Please check your information.');
      }
      // Handle other errors
      else {
        toast.error(result.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-navy">
      {/* Spacer for fixed navbar */}
      <div className="h-[116px]"></div>
      
      {/* 1. Banner */}
      <section className="min-h-screen bg-brand-navy flex items-center py-16 sm:py-20 overflow-hidden">
        <div className="container mx-auto px-3 sm:px-4 md:px-6 max-w-7xl w-full">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 items-start w-full">
            {/* FORM FIRST ON MOBILE - Order changes using flexbox order */}
            <div className="text-white w-full overflow-hidden order-2 lg:order-1">
              <div className="inline-block mb-4 sm:mb-6 px-3 sm:px-5 py-1.5 sm:py-2 border-2 border-blue-500/30 rounded-full bg-blue-500/5">
                <div className="flex items-center gap-2">
                  <Shield className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />
                  <span className="text-blue-300 font-medium text-xs sm:text-sm">Trusted Brand Protection</span>
                </div>
              </div>
              
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-heading font-bold mb-3 sm:mb-4 leading-tight break-words">
                Protect Your Brand's <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600">Identity & Future</span>
              </h1>
              
              <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-6 sm:mb-8 leading-relaxed">
                Expert trademark registration services starting at just $45. We handle everything from comprehensive searches to USPTO filing.
              </p>

              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-1">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm sm:text-base md:text-lg mb-0.5 sm:mb-1">Fast 24-Hour Filing</h3>
                    <p className="text-gray-400 text-xs sm:text-sm break-words">Get your trademark application filed with USPTO within 24 hours.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-1">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm sm:text-base md:text-lg mb-0.5 sm:mb-1">98% Success Rate</h3>
                    <p className="text-gray-400 text-xs sm:text-sm break-words">Our expert attorneys ensure maximum approval chances.</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-1">
                    <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm sm:text-base md:text-lg mb-0.5 sm:mb-1">Complete Legal Protection</h3>
                    <p className="text-gray-400 text-xs sm:text-sm break-words">Nationwide exclusive rights and ongoing monitoring.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Button
                  onClick={() => openPopup('home')}
                  data-testid="hero-get-started-button"
                  size="lg"
                  className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold px-4 sm:px-6 py-3 sm:py-5 transition-transform hover:scale-105 shadow-xl text-sm sm:text-base w-full sm:w-auto"
                >
                  Get Started Now
                  <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
                <Button
                  onClick={() => {
                    if (window.Tawk_API) window.Tawk_API.maximize();
                  }}
                  data-testid="hero-live-chat-button"
                  size="lg"
                  variant="outline"
                  className="border-2 border-blue-500 text-blue-400 hover:bg-blue-500/10 font-semibold px-4 sm:px-6 py-3 sm:py-5 transition-all w-full sm:w-auto text-sm sm:text-base"
                >
                  Live Chat
                </Button>
              </div>
            </div>

            <Card className="shadow-2xl border border-blue-900/30 bg-brand-navy-card max-w-full lg:max-w-md xl:max-w-lg w-full order-1 lg:order-2">
              <CardContent className="p-4 sm:p-6 md:p-6 lg:p-6">
                <h3 className="text-base sm:text-xl md:text-2xl lg:text-3xl font-heading font-bold text-white mb-1 text-center leading-tight px-1">
                  Register Your Trademark for Only <span className="animate-pulse text-amber-400">$45</span>
                </h3>
                <p className="text-gray-400 mb-2 sm:mb-3 text-center text-xs sm:text-sm">Protect your brand with trusted expertise</p>
                {geoData?.data?.countryName && (
                  <p className="text-blue-400 text-[10px] sm:text-xs text-center mb-2 sm:mb-3">
                    📍 Detected location: {geoData.data.countryName}
                  </p>
                )}
                
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 sm:space-y-4">
                  <div>
                    <Label htmlFor="home-name" className="text-sm font-medium text-gray-300 mb-1 block">
                      Name <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="home-name"
                      data-testid="home-form-name-input"
                      placeholder="Enter your full name"
                      {...register('name')}
                      className="h-10 bg-brand-navy border-blue-900/50 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500"
                    />
                    {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name.message}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="home-email" className="text-sm font-medium text-gray-300 mb-1 block">
                      Email <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="home-email"
                      data-testid="home-form-email-input"
                      type="email"
                      placeholder="Enter your email address"
                      {...register('email')}
                      className="h-10 bg-brand-navy border-blue-900/50 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500"
                    />
                    {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="home-contact" className="text-sm font-medium text-gray-300 mb-1 block">
                      Contact Number <span className="text-red-400">*</span>
                    </Label>
                    <div className="flex gap-2">
                      <Select defaultValue="+1" onValueChange={(value) => setValue('country_code', value)}>
                        <SelectTrigger className="w-20 h-10 bg-brand-navy border-blue-900/50 text-white" data-testid="home-form-country-code-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-brand-navy-card border-blue-900/50">
                          <SelectItem value="+1" className="text-white">🇺🇸 +1</SelectItem>
                          <SelectItem value="+44" className="text-white">🇬🇧 +44</SelectItem>
                          <SelectItem value="+91" className="text-white">🇮🇳 +91</SelectItem>
                          <SelectItem value="+61" className="text-white">🇦🇺 +61</SelectItem>
                          <SelectItem value="+86" className="text-white">🇨🇳 +86</SelectItem>
                          <SelectItem value="+81" className="text-white">🇯🇵 +81</SelectItem>
                          <SelectItem value="+49" className="text-white">🇩🇪 +49</SelectItem>
                          <SelectItem value="+33" className="text-white">🇫🇷 +33</SelectItem>
                          <SelectItem value="+39" className="text-white">🇮🇹 +39</SelectItem>
                          <SelectItem value="+34" className="text-white">🇪🇸 +34</SelectItem>
                          <SelectItem value="+7" className="text-white">🇷🇺 +7</SelectItem>
                          <SelectItem value="+55" className="text-white">🇧🇷 +55</SelectItem>
                          <SelectItem value="+52" className="text-white">🇲🇽 +52</SelectItem>
                          <SelectItem value="+82" className="text-white">🇰🇷 +82</SelectItem>
                          <SelectItem value="+31" className="text-white">🇳🇱 +31</SelectItem>
                          <SelectItem value="+46" className="text-white">🇸🇪 +46</SelectItem>
                          <SelectItem value="+41" className="text-white">🇨🇭 +41</SelectItem>
                          <SelectItem value="+47" className="text-white">🇳🇴 +47</SelectItem>
                          <SelectItem value="+45" className="text-white">🇩🇰 +45</SelectItem>
                          <SelectItem value="+48" className="text-white">🇵🇱 +48</SelectItem>
                          <SelectItem value="+65" className="text-white">🇸🇬 +65</SelectItem>
                          <SelectItem value="+60" className="text-white">🇲🇾 +60</SelectItem>
                          <SelectItem value="+66" className="text-white">🇹🇭 +66</SelectItem>
                          <SelectItem value="+84" className="text-white">🇻🇳 +84</SelectItem>
                          <SelectItem value="+62" className="text-white">🇮🇩 +62</SelectItem>
                          <SelectItem value="+63" className="text-white">🇵🇭 +63</SelectItem>
                          <SelectItem value="+92" className="text-white">🇵🇰 +92</SelectItem>
                          <SelectItem value="+880" className="text-white">🇧🇩 +880</SelectItem>
                          <SelectItem value="+971" className="text-white">🇦🇪 +971</SelectItem>
                          <SelectItem value="+966" className="text-white">🇸🇦 +966</SelectItem>
                          <SelectItem value="+27" className="text-white">🇿🇦 +27</SelectItem>
                          <SelectItem value="+234" className="text-white">🇳🇬 +234</SelectItem>
                          <SelectItem value="+20" className="text-white">🇪🇬 +20</SelectItem>
                          <SelectItem value="+254" className="text-white">🇰🇪 +254</SelectItem>
                          <SelectItem value="+64" className="text-white">🇳🇿 +64</SelectItem>
                          <SelectItem value="+353" className="text-white">🇮🇪 +353</SelectItem>
                          <SelectItem value="+351" className="text-white">🇵🇹 +351</SelectItem>
                          <SelectItem value="+30" className="text-white">🇬🇷 +30</SelectItem>
                          <SelectItem value="+32" className="text-white">🇧🇪 +32</SelectItem>
                          <SelectItem value="+43" className="text-white">🇦🇹 +43</SelectItem>
                          <SelectItem value="+358" className="text-white">🇫🇮 +358</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        id="home-contact"
                        data-testid="home-form-contact-input"
                        type="tel"
                        placeholder="Phone number"
                        maxLength={10}
                        {...register('contact_number')}
                        onKeyPress={(e) => {
                          // Only allow digits
                          if (!/[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onPaste={(e) => {
                          // Prevent pasting non-numeric content
                          const pastedData = e.clipboardData.getData('text');
                          if (!/^\d+$/.test(pastedData)) {
                            e.preventDefault();
                          }
                        }}
                        className="flex-1 h-10 bg-brand-navy border-blue-900/50 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    {errors.contact_number && <p className="text-xs text-red-400 mt-1">{errors.contact_number.message}</p>}
                  </div>
                  
                  <div>
                    <Label htmlFor="home-message" className="text-sm font-medium text-gray-300 mb-1 block">
                      Description <span className="text-red-400">*</span>
                    </Label>
                    <Textarea
                      id="home-message"
                      data-testid="home-form-message-input"
                      placeholder="Tell us about your needs..."
                      {...register('message')}
                      className="bg-brand-navy border-blue-900/50 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500 text-sm"
                      rows={3}
                    />
                    {errors.message && <p className="text-xs text-red-400 mt-1">{errors.message.message}</p>}
                  </div>
                  
                  <Button
                    type="submit"
                    data-testid="home-form-submit-button"
                    disabled={isSubmitting}
                    className="w-full bg-brand-blue hover:bg-brand-blue-hover text-white font-semibold py-5 h-auto transition-transform hover:scale-[1.02] shadow-xl"
                  >
                    {isSubmitting ? 'Submitting...' : 'Start Your Registration'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* 2. Why Trademark is Important - Blue Background */}
      <section className="py-20 md:py-32 bg-brand-navy">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <div className="flex flex-col">
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=85&w=800&h=800&fit=crop"
                alt="Why Trademark is Important"
                className="rounded-2xl shadow-2xl w-full h-full object-cover"
              />
            </div>
            <div className="text-white flex flex-col justify-between">
              <div>
                <p className="text-blue-400 font-semibold mb-2 uppercase tracking-wide">PROTECT YOUR BRAND</p>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
                  Why Trademark Registration is Critical for Your Business
                </h2>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-base md:text-lg mb-1">Exclusive Legal Rights</h3>
                      <p className="text-gray-400">Gain nationwide protection and exclusive rights to use your brand name, logo, and slogan in your industry.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-base md:text-lg mb-1">Prevent Copycats</h3>
                      <p className="text-gray-400">Stop competitors from using similar names or logos that could confuse customers and damage your reputation.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-base md:text-lg mb-1">Build Brand Value</h3>
                      <p className="text-gray-400">A registered trademark becomes a valuable business asset that increases your company's worth and credibility.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-base md:text-lg mb-1">Legal Foundation</h3>
                      <p className="text-gray-400">Establish a strong legal foundation to take action against infringers and protect your brand in court if needed.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <Button
                  onClick={() => openPopup('home')}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-5"
                >
                  Protect Your Brand Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Services */}
      <section className="py-20 md:py-32 bg-brand-navy">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4 break-words">
              Our Services
            </h2>
            <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto break-words">
              Comprehensive intellectual property protection for your business
            </p>
          </div>

          {/* Desktop/Tablet Grid View (shows at md: 768px+) */}
          <div className="max-md:hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, idx) => (
              <div
                key={idx}
                className="p-8 bg-brand-navy-card rounded-xl border border-blue-900/30 hover:border-blue-500/50 transition-all hover:-translate-y-2"
              >
                <div className="w-14 h-14 bg-blue-500/10 rounded-lg flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          {/* Mobile Slider View */}
          <div className="md:hidden">
            <Slider {...mobileSliderSettings}>
              {services.map((service, idx) => (
                <div key={idx} className="px-2">
                  <div className="p-8 bg-brand-navy-card rounded-xl border border-blue-900/30 mx-auto max-w-sm">
                    <div className="w-14 h-14 bg-blue-500/10 rounded-lg flex items-center justify-center mb-6">
                      <service.icon className="w-7 h-7 text-blue-400" />
                    </div>
                    <h3 className="text-2xl font-heading font-bold text-white mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </section>

      {/* 4. Process */}
      <Process />

      {/* 5. Pricing */}
      <Pricing />

      {/* 6. Why Choose Us */}
      <section className="py-20 md:py-32 bg-brand-navy">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <div className="text-white">
              <p className="text-blue-400 font-semibold mb-2 uppercase tracking-wide">WHY CHOOSE US</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6 break-words">
                Your Trusted Partner in Brand Protection
              </h2>
              
              {/* Desktop/Tablet View - Stacked Items (shows at md: 768px+) */}
              <div className="max-md:hidden space-y-4 md:space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-2xl md:text-3xl mb-2">98% Success Rate</h3>
                      <p className="text-gray-400">Our expert team has achieved a 98% approval rate on trademark applications, ensuring your brand gets protected right the first time.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Lock className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-2xl md:text-3xl mb-2">Comprehensive Protection</h3>
                      <p className="text-gray-400">From initial search to ongoing monitoring, we provide end-to-end trademark services to keep your brand secure at every stage.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Target className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-2xl md:text-3xl mb-2">Expert Legal Team</h3>
                      <p className="text-gray-400">Work with experienced trademark attorneys who have successfully filed over 15,000 trademarks across all industries.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-2xl md:text-3xl mb-2">Fast & Affordable</h3>
                      <p className="text-gray-400">Get your application filed within 24 hours with packages starting at just $45, plus government fees. No hidden costs, no surprises.</p>
                    </div>
                  </div>
                </div>

                {/* Mobile View - Show stacked items (NO SLIDER) */}
                <div className="md:hidden mt-6 space-y-6">
                  <div className="flex items-start gap-4 bg-brand-navy-card p-6 rounded-xl border border-blue-900/30">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2">98% Success Rate</h3>
                      <p className="text-gray-400 text-sm">Our expert team has achieved a 98% approval rate on trademark applications, ensuring your brand gets protected right the first time.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 bg-brand-navy-card p-6 rounded-xl border border-blue-900/30">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Lock className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2">Comprehensive Protection</h3>
                      <p className="text-gray-400 text-sm">From initial search to ongoing monitoring, we provide end-to-end trademark services to keep your brand secure at every stage.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 bg-brand-navy-card p-6 rounded-xl border border-blue-900/30">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Target className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2">Expert Legal Team</h3>
                      <p className="text-gray-400 text-sm">Work with experienced trademark attorneys who have successfully filed over 15,000 trademarks across all industries.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 bg-brand-navy-card p-6 rounded-xl border border-blue-900/30">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2">Fast & Affordable</h3>
                      <p className="text-gray-400 text-sm">Get your application filed within 24 hours with packages starting at just $45, plus government fees. No hidden costs, no surprises.</p>
                    </div>
                  </div>
                </div>
            </div>
            <div className="flex flex-col">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?q=85&w=800&h=800&fit=crop"
                alt="Why Choose Us"
                className="rounded-2xl shadow-2xl w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 7. CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
            Don't Risk Your Brand's Future!
          </h2>
          <p className="text-base md:text-lg mb-8 text-purple-100">
            Unregistered trademarks can lead to costly legal battles. Protect your brand now before it's too late!
          </p>
          <Button
            onClick={() => openPopup('home')}
            data-testid="cta-get-started-button"
            size="lg"
            className="bg-white text-purple-700 hover:bg-purple-50 font-semibold text-lg px-8 py-6 transition-transform hover:scale-105 shadow-xl"
          >
            Start Your Application
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* 8. FAQs */}
      <section className="py-20 md:py-32 bg-brand-navy">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-base md:text-lg text-gray-400">
              Everything you need to know about trademark registration
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`} className="border border-blue-900/30 bg-brand-navy-card rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-white hover:text-blue-400">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-400 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 9. Testimonials */}
      <Testimonials />
    </div>
  );
};
